import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2, AlertCircle, ExternalLink, Calendar } from 'lucide-react';
import { ELIGIBILITY_PROCESS_TYPES } from '@/lib/qualificationOptions';
import { useDealEligibility } from '@/hooks/useDealEligibility';

interface PostMeetingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (data: {
    meetingHappened: boolean;
    noShowReason?: string;
    meetingNotes?: string;
    proposalValue?: number;
    recommendedProcess?: string;
  }) => void;
  dealId: string;
  isLoading?: boolean;
  onGoToEligibility?: () => void;
}

export function PostMeetingModal({
  isOpen,
  onClose,
  onConfirm,
  dealId,
  isLoading = false,
  onGoToEligibility,
}: PostMeetingModalProps) {
  const { t } = useTranslation();
  const { eligibilities } = useDealEligibility(dealId);
  
  const [meetingHappened, setMeetingHappened] = useState(true);
  const [noShowReason, setNoShowReason] = useState('');
  const [meetingNotes, setMeetingNotes] = useState('');
  const [proposalValue, setProposalValue] = useState('');
  const [recommendedProcess, setRecommendedProcess] = useState('');

  const hasEligibility = eligibilities.length > 0;

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setMeetingHappened(true);
      setNoShowReason('');
      setMeetingNotes('');
      setProposalValue('');
      setRecommendedProcess('');
    }
  }, [isOpen]);

  const handleConfirm = () => {
    if (meetingHappened) {
      const value = parseFloat(proposalValue.replace(/[^0-9.]/g, ''));
      if (!value || !recommendedProcess) return;
      
      onConfirm({
        meetingHappened: true,
        meetingNotes,
        proposalValue: value,
        recommendedProcess,
      });
    } else {
      if (!noShowReason.trim()) return;
      onConfirm({
        meetingHappened: false,
        noShowReason,
      });
    }
  };

  const handleClose = () => {
    onClose();
  };

  const formatCurrency = (value: string) => {
    const digits = value.replace(/[^\d]/g, '');
    const number = parseInt(digits) / 100;
    if (isNaN(number) || number === 0) return '';
    return number.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const handleProposalValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setProposalValue(formatCurrency(e.target.value));
  };

  const isValidForMeeting = meetingHappened && 
    proposalValue && 
    parseFloat(proposalValue.replace(/[^0-9.]/g, '')) > 0 && 
    recommendedProcess;

  const isValidForNoShow = !meetingHappened && noShowReason.trim();

  const isValid = meetingHappened ? isValidForMeeting : isValidForNoShow;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-purple-100 dark:bg-purple-900/30">
              <Calendar className="h-5 w-5 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <DialogTitle>{t('stageTransition.postMeetingTitle')}</DialogTitle>
              <DialogDescription>
                {t('stageTransition.postMeetingDescription')}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Meeting happened toggle */}
          <div className="flex items-center justify-between rounded-lg border p-4">
            <div className="space-y-0.5">
              <Label className="text-base">{t('stageTransition.meetingHappened')}</Label>
            </div>
            <Switch
              checked={meetingHappened}
              onCheckedChange={setMeetingHappened}
            />
          </div>

          {!meetingHappened ? (
            /* No-show form */
            <div className="space-y-4 rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-900/20">
              <div className="flex items-center gap-2 text-amber-700 dark:text-amber-300">
                <AlertCircle className="h-4 w-4" />
                <span className="text-sm font-medium">
                  {t('stageTransition.noShowRegistered')}
                </span>
              </div>
              <div className="space-y-2">
                <Label htmlFor="noshow-reason">{t('stageTransition.noShowReason')} *</Label>
                <Textarea
                  id="noshow-reason"
                  value={noShowReason}
                  onChange={(e) => setNoShowReason(e.target.value)}
                  placeholder={t('stageTransition.noShowReasonPlaceholder') || 'Descreva o motivo do no-show...'}
                  rows={2}
                />
              </div>
            </div>
          ) : (
            /* Meeting happened form */
            <div className="space-y-4">
              {/* Eligibility warning */}
              {!hasEligibility && (
                <Alert className="border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-900/20">
                  <AlertCircle className="h-4 w-4 text-amber-600" />
                  <AlertDescription className="flex items-center justify-between">
                    <span className="text-amber-800 dark:text-amber-200">
                      {t('stageTransition.eligibilityWarning')}
                    </span>
                    {onGoToEligibility && (
                      <Button
                        variant="link"
                        size="sm"
                        onClick={() => {
                          handleClose();
                          onGoToEligibility();
                        }}
                        className="text-amber-700 dark:text-amber-300"
                      >
                        {t('stageTransition.goToEligibility')}
                        <ExternalLink className="ml-1 h-3 w-3" />
                      </Button>
                    )}
                  </AlertDescription>
                </Alert>
              )}

              {/* Meeting notes */}
              <div className="space-y-2">
                <Label htmlFor="meeting-notes">{t('stageTransition.meetingNotes')}</Label>
                <Textarea
                  id="meeting-notes"
                  value={meetingNotes}
                  onChange={(e) => setMeetingNotes(e.target.value)}
                  placeholder={t('stageTransition.meetingNotesPlaceholder') || 'Notas da reunião...'}
                  rows={3}
                />
              </div>

              {/* Proposal value */}
              <div className="space-y-2">
                <Label htmlFor="proposal-value">{t('stageTransition.proposalValue')} *</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                    $
                  </span>
                  <Input
                    id="proposal-value"
                    value={proposalValue}
                    onChange={handleProposalValueChange}
                    placeholder="0.00"
                    className="pl-7"
                  />
                </div>
              </div>

              {/* Recommended process */}
              <div className="space-y-2">
                <Label htmlFor="recommended-process">
                  {t('stageTransition.recommendedProcess')} *
                </Label>
                <Select value={recommendedProcess} onValueChange={setRecommendedProcess}>
                  <SelectTrigger id="recommended-process">
                    <SelectValue placeholder={t('common.selectOption')} />
                  </SelectTrigger>
                  <SelectContent>
                    {ELIGIBILITY_PROCESS_TYPES.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isLoading}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!isValid || isLoading}
            variant={meetingHappened ? 'default' : 'secondary'}
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('common.loading')}
              </>
            ) : meetingHappened ? (
              t('stageTransition.sendProposal')
            ) : (
              t('stageTransition.registerNoShow')
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default PostMeetingModal;
